import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Building2, Mail, Lock, User, Phone, Briefcase, Eye, EyeOff, ArrowLeft, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const Register = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    company_name: '',
    phone: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedRole) {
      toast.error('Por favor selecciona un tipo de cuenta');
      return;
    }

    setLoading(true);

    try {
      await register({
        ...formData,
        role: selectedRole
      });
      toast.success('Cuenta creada exitosamente');
      navigate('/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al crear cuenta';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const roles = [
    {
      id: 'desarrollador',
      title: 'Desarrollador',
      description: 'Empresa desarrolladora inmobiliaria. Crea obras, gestiona catálogos y aprueba avances.',
      icon: Building2
    },
    {
      id: 'constructor',
      title: 'Constructor',
      description: 'Contratista de obra. Cotiza, captura avances y sube evidencias.',
      icon: Briefcase
    }
  ];

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex">
      {/* Left Side - Form */}
      <div className="flex-1 flex flex-col justify-center px-4 sm:px-6 lg:px-8 py-12 overflow-auto">
        <div className="sm:mx-auto sm:w-full sm:max-w-lg">
          <Link to="/" className="inline-flex items-center gap-2 text-[#5C554B] hover:text-[#C05621] mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Volver al inicio
          </Link>

          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-[#C05621] rounded-xl flex items-center justify-center">
              <Building2 className="w-7 h-7 text-white" />
            </div>
            <span className="font-bold text-2xl text-[#2D2A26]">BROCO</span>
          </div>

          <h1 className="text-3xl font-bold text-[#2D2A26] mb-2">Crear cuenta</h1>
          <p className="text-[#5C554B] mb-8">Completa tus datos para comenzar</p>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Role Selection */}
            <div className="space-y-3">
              <Label className="text-[#2D2A26] font-medium">Tipo de cuenta</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {roles.map((role) => (
                  <button
                    key={role.id}
                    type="button"
                    onClick={() => setSelectedRole(role.id)}
                    className={`
                      relative p-4 rounded-xl border-2 text-left transition-all
                      ${selectedRole === role.id 
                        ? 'border-[#C05621] bg-[#C05621]/5' 
                        : 'border-[#E2E8F0] hover:border-[#C05621]/50'
                      }
                    `}
                    data-testid={`role-${role.id}`}
                  >
                    {selectedRole === role.id && (
                      <div className="absolute top-3 right-3 w-6 h-6 bg-[#C05621] rounded-full flex items-center justify-center">
                        <Check className="w-4 h-4 text-white" />
                      </div>
                    )}
                    <role.icon className={`w-8 h-8 mb-3 ${selectedRole === role.id ? 'text-[#C05621]' : 'text-[#5C554B]'}`} />
                    <h3 className="font-semibold text-[#2D2A26] mb-1">{role.title}</h3>
                    <p className="text-sm text-[#5C554B]">{role.description}</p>
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-[#2D2A26] font-medium">
                  Nombre completo
                </Label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9CA3AF]" />
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Tu nombre"
                    className="input-default pl-12"
                    required
                    data-testid="name-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="company_name" className="text-[#2D2A26] font-medium">
                  Empresa
                </Label>
                <div className="relative">
                  <Briefcase className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9CA3AF]" />
                  <Input
                    id="company_name"
                    name="company_name"
                    value={formData.company_name}
                    onChange={handleChange}
                    placeholder="Nombre de empresa"
                    className="input-default pl-12"
                    data-testid="company-input"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#2D2A26] font-medium">
                Correo electrónico
              </Label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9CA3AF]" />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="tu@email.com"
                  className="input-default pl-12"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone" className="text-[#2D2A26] font-medium">
                Teléfono (opcional)
              </Label>
              <div className="relative">
                <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9CA3AF]" />
                <Input
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+52 55 1234 5678"
                  className="input-default pl-12"
                  data-testid="phone-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-[#2D2A26] font-medium">
                Contraseña
              </Label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9CA3AF]" />
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Mínimo 6 caracteres"
                  className="input-default pl-12 pr-12"
                  required
                  minLength={6}
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#5C554B] transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button 
              type="submit" 
              className="btn-primary w-full h-12 text-base"
              disabled={loading || !selectedRole}
              data-testid="register-submit"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                'Crear cuenta'
              )}
            </Button>
          </form>

          <p className="mt-8 text-center text-[#5C554B]">
            ¿Ya tienes cuenta?{' '}
            <Link to="/login" className="text-[#C05621] font-semibold hover:underline">
              Inicia sesión
            </Link>
          </p>
        </div>
      </div>

      {/* Right Side - Image */}
      <div className="hidden lg:block lg:flex-1 relative">
        <img
          src="https://images.unsplash.com/photo-1758876734777-dcc6981f3671?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHxjb25zdHJ1Y3Rpb24lMjB3b3JrZXIlMjB1c2luZyUyMHBob25lfGVufDB8fHx8MTc3MDI0MzgxOXww&ixlib=rb-4.1.0&q=85"
          alt="Constructor usando teléfono"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#2D2A26]/40" />
        <div className="absolute bottom-12 left-12 right-12">
          <blockquote className="text-white">
            <p className="text-2xl font-medium mb-4">
              "La herramienta que necesitábamos para coordinarnos con desarrolladores de forma profesional."
            </p>
            <footer className="text-white/80">
              — Contratista de Obra
            </footer>
          </blockquote>
        </div>
      </div>
    </div>
  );
};

export default Register;
